/**
 * RELIRE LES EXERCICES D'UN CHAPITRE — refonte de la console, lot L3
 * (`/contenus/:code/exercices`, maquette 03, retour du fondateur du
 * 15 octobre 2026).
 *
 * « Il faut descendre la page, choisir une ligne, redescendre pour voir le
 * détail ; aucune figure ; aucun aperçu. » Cet écran est un ESPACE DE TRAVAIL
 * à deux volets qui tient dans la fenêtre — la page ne défile jamais :
 *
 *  - à gauche, la file compacte : filtres « À relire / À corriger / Relus /
 *    Tous » (avec leurs comptes), la notion, une recherche, la progression
 *    « 12 / 105 » ; la liste défile seule ;
 *  - à droite, l'exercice TEL QUE L'ÉLÈVE LE VOIT (formules et figures, rendu
 *    du produit) et ce que Silex attend (bonne réponse, démarche, indices,
 *    notions) ; ce volet défile seul si l'exercice est long ;
 *  - en bas à droite, la barre d'actions, TOUJOURS visible : « Relu, rien à
 *    changer » (R), « À corriger… » (C, note obligatoire, sur place), précédent
 *    et suivant (K, J). Après une décision, l'exercice suivant s'ouvre.
 *
 * D-1 : un exercice ne s'édite pas ici ; « à corriger » envoie la reprise vers
 * la source. D-4 : ouvert aux tuteurs. La relecture ne touche pas au service
 * (I-1) ; RETIRER du service et y REMETTRE sont deux gestes à part, réservés à
 * l'admin (décision du 15 octobre 2026), avec motif et confirmation dans la
 * page — jamais `confirm()`.
 *
 * Choisir un exercice ne fait jamais défiler la page : la liste fait défiler
 * SON conteneur, à la main, et le volet de droite revient en haut.
 *
 * LA FILE EST PAGINÉE PAR 20 (AB#888, retour du fondateur du 17 oct. : 91
 * exercices en une colonne). La page suit l'exercice courant : J/K et le
 * passage automatique après une décision franchissent les pages d'eux-mêmes.
 * Dans l'adresse, `ex` l'emporte (la page est celle où il tombe) ; sans `ex`,
 * `page` dit quelle page montrer, et l'exercice courant en est le premier.
 * Changer d'état, de notion ou de recherche ramène à la page 1. L'ordre de la
 * file reste notion par notion ; chaque ligne porte sa notion, si bien qu'une
 * page qui commence au milieu d'une notion la nomme quand même.
 */
import type { ExerciceRelectureResponse, ExercicesChapitreResponse } from '@silex/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ErreurRessource } from '../components/EtatRessource';
import { Pagination } from '../components/Pagination';
import { ExerciceARelire } from '../components/relecture/ExerciceARelire';
import { RetraitDuService } from '../components/relecture/RetraitDuService';
import { TexteMaths } from '../components/TexteMaths';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { PARAM_PAGE, lirePage } from '../lib/pagination';
import {
  FILTRES_FILE,
  NOM_ETAT,
  NOM_FILTRE,
  actionDeTouche,
  apresDecision,
  chapeau,
  continuerGeste,
  deplacer,
  direProgression,
  etatDansLaFile,
  gesteDeService,
  lignesVisibles,
  notionsDeLaFile,
  ordonner,
  ouvrirGeste,
  pageDeLaFile,
  progression,
  type EtapeService,
  type FiltreFile,
  type LigneFile,
} from '../lib/relecture-exercices';
import { messageErreur, useAsync } from '../lib/useAsync';

import './contenus.css';
import './relecture.css';

type Detail =
  | { readonly etat: 'charge'; readonly data: ExerciceRelectureResponse }
  | { readonly etat: 'erreur'; readonly message: string };

const TON_ETAT = {
  a_relire: 'badge--en-cours',
  a_corriger: 'badge--a-revoir',
  relu: 'badge--acquis',
} as const;

function estUnChamp(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable;
}

export function RelectureExercicesPage() {
  const { code = '' } = useParams<{ code: string }>();
  const { user } = useAuth();
  const actif = manuelVivantActif();
  const loader = useMemo(
    () => (actif && code ? () => api.manuelExercicesChapitre(code) : null),
    [actif, code],
  );
  const reponse = useAsync<ExercicesChapitreResponse>(loader);

  /* La file, tenue à jour après chaque geste sans relire tout le chapitre. */
  const [lignes, setLignes] = useState<readonly LigneFile[]>([]);
  useEffect(() => {
    if (reponse.status === 'ready') setLignes(ordonner(reponse.data.exercices));
  }, [reponse]);

  /* Filtres et exercice courant : dans l'adresse (un lien s'envoie). */
  const [params, setParams] = useSearchParams();
  const etat = (FILTRES_FILE as readonly string[]).includes(params.get('etat') ?? '')
    ? (params.get('etat') as FiltreFile)
    : 'a_relire';
  const notion = params.get('notion');
  const [recherche, setRecherche] = useState('');
  const poser = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === '') n.delete(k);
            else n.set(k, v);
          }
          return n;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const visibles = useMemo(
    () => lignesVisibles(lignes, { etat, notion, recherche }),
    [lignes, etat, notion, recherche],
  );
  /* La page montrée : celle de l'exercice demandé, sinon celle de l'adresse. */
  const { decoupe, courant } = pageDeLaFile(visibles, params.get('ex'), lirePage(params));
  /* Choisir un exercice : la page de l'adresse est recalée plus bas (effet). */
  const choisir = useCallback((c: string | null) => poser({ ex: c }), [poser]);
  const allerALaPage = useCallback(
    (n: number) => poser({ [PARAM_PAGE]: n > 1 ? String(n) : null, ex: null }),
    [poser],
  );
  /* L'adresse dit toujours la page affichée — un lien renvoyé tombe juste. */
  const pageAdresse = params.get(PARAM_PAGE);
  useEffect(() => {
    const attendue = decoupe.page > 1 ? String(decoupe.page) : null;
    if (reponse.status === 'ready' && pageAdresse !== attendue) {
      poser({ [PARAM_PAGE]: attendue });
    }
  }, [decoupe.page, pageAdresse, poser, reponse.status]);

  /* Le détail de l'exercice courant, gardé en mémoire ; le suivant est préchargé. */
  const [details, setDetails] = useState<ReadonlyMap<string, Detail>>(new Map());
  const charges = useRef(new Set<string>());
  const charger = useCallback((c: string) => {
    if (charges.current.has(c)) return;
    charges.current.add(c);
    api
      .exerciceRelecture(c)
      .then((data) => setDetails((m) => new Map(m).set(c, { etat: 'charge', data })))
      .catch((err: unknown) => {
        charges.current.delete(c);
        setDetails((m) => new Map(m).set(c, { etat: 'erreur', message: messageErreur(err) }));
      });
  }, []);
  useEffect(() => {
    if (courant === null) return;
    charger(courant);
    const suivant = deplacer(visibles, courant, 1);
    if (suivant !== null && suivant !== courant) charger(suivant);
  }, [courant, visibles, charger]);

  /* Le volet de droite revient en haut ; la liste montre le courant — en
     faisant défiler LEUR conteneur, jamais la page. */
  const volet = useRef<HTMLDivElement>(null);
  const liste = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (volet.current) volet.current.scrollTop = 0;
    const ul = liste.current;
    const li = ul?.querySelector<HTMLElement>('[aria-current="true"]');
    if (ul && li) {
      const haut = li.offsetTop - ul.offsetTop;
      if (haut < ul.scrollTop) ul.scrollTop = haut;
      else if (haut + li.offsetHeight > ul.scrollTop + ul.clientHeight) {
        ul.scrollTop = haut + li.offsetHeight - ul.clientHeight;
      }
    }
  }, [courant]);

  /* Les gestes. */
  const [noteOuverte, setNoteOuverte] = useState(false);
  const [note, setNote] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const champNote = useRef<HTMLTextAreaElement>(null);
  /* Retirer du service / remettre en service (admin) : l'étape et le motif. */
  const [etapeService, setEtapeService] = useState<EtapeService>('ferme');
  const [motifService, setMotifService] = useState('');
  useEffect(() => {
    setNoteOuverte(false);
    setNote('');
    setErreur(null);
    setEtapeService('ferme');
    setMotifService('');
  }, [courant]);

  const appliquer = useCallback(
    async (
      geste: () => Promise<{
        statutRelecture: LigneFile['statutRelecture'];
        noteRelecture: string | null;
      }>,
    ) => {
      if (courant === null || enCours) return;
      setEnCours(true);
      setErreur(null);
      try {
        const r = await geste();
        const apres = lignes.map((l) =>
          l.code === courant
            ? { ...l, statutRelecture: r.statutRelecture, noteRelecture: r.noteRelecture }
            : l,
        );
        setLignes(apres);
        setDetails((m) => {
          const d = m.get(courant);
          if (d?.etat !== 'charge') return m;
          return new Map(m).set(courant, {
            etat: 'charge',
            data: {
              ...d.data,
              exercice: {
                ...d.data.exercice,
                statutRelecture: r.statutRelecture,
                noteRelecture: r.noteRelecture,
              },
            },
          });
        });
        /* Passage automatique au suivant. */
        choisir(
          apresDecision(visibles, lignesVisibles(apres, { etat, notion, recherche }), courant),
        );
      } catch (err: unknown) {
        setErreur(messageErreur(err));
      } finally {
        setEnCours(false);
      }
    },
    [courant, enCours, lignes, visibles, etat, notion, recherche, choisir],
  );

  const relu = useCallback(
    () => void appliquer(() => api.relireExercice(courant!, { verdict: 'relu' })),
    [appliquer, courant],
  );
  const ouvrirNote = useCallback(() => {
    setNoteOuverte(true);
    /* Le champ s'ouvre SUR PLACE, et prend la main. */
    window.setTimeout(() => champNote.current?.focus({ preventScroll: true }), 0);
  }, []);
  const enregistrerACorriger = useCallback(() => {
    const n = note.trim();
    if (n === '') {
      setErreur('« À corriger » demande une note : dites ce qui est à reprendre dans la source.');
      champNote.current?.focus({ preventScroll: true });
      return;
    }
    void appliquer(() => api.relireExercice(courant!, { verdict: 'a_corriger', note: n }));
  }, [appliquer, courant, note]);
  const remettre = useCallback(
    () => void appliquer(() => api.remettreExerciceARelire(courant!)),
    [appliquer, courant],
  );

  /* Le service : on reste sur l'exercice (son état de relecture n'a pas bougé). */
  const confirmerService = useCallback(async () => {
    if (courant === null || enCours) return;
    const ligne = lignes.find((l) => l.code === courant);
    if (ligne === undefined) return;
    const geste = gesteDeService(user?.role, ligne.statut);
    if (geste === null) return;
    setEnCours(true);
    setErreur(null);
    try {
      const r =
        geste === 'retirer'
          ? await api.retirerExercice(courant, motifService.trim())
          : await api.remettreExerciceEnService(courant);
      setLignes((ls) =>
        ls.map((l) =>
          l.code === courant ? { ...l, statut: r.statut, motifRetrait: r.motifRetrait } : l,
        ),
      );
      setDetails((m) => {
        const d = m.get(courant);
        if (d?.etat !== 'charge') return m;
        return new Map(m).set(courant, {
          etat: 'charge',
          data: {
            ...d.data,
            exercice: { ...d.data.exercice, statut: r.statut, motifRetrait: r.motifRetrait },
          },
        });
      });
      setEtapeService('ferme');
      setMotifService('');
    } catch (err: unknown) {
      setErreur(messageErreur(err));
    } finally {
      setEnCours(false);
    }
  }, [courant, enCours, lignes, user?.role, motifService]);

  /* Le clavier : J / K, R, C. */
  useEffect(() => {
    const surTouche = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && noteOuverte) {
        setNoteOuverte(false);
        (document.activeElement as HTMLElement | null)?.blur();
        return;
      }
      /* Pendant un retrait ou une remise en service, le clavier ne marque rien. */
      if (etapeService !== 'ferme') {
        if (ev.key === 'Escape') setEtapeService('ferme');
        return;
      }
      const a = actionDeTouche({
        key: ev.key,
        ctrlKey: ev.ctrlKey,
        metaKey: ev.metaKey,
        altKey: ev.altKey,
        dansUnChamp: estUnChamp(ev.target),
      });
      if (a === null || courant === null) return;
      ev.preventDefault();
      if (a === 'suivant') choisir(deplacer(visibles, courant, 1));
      else if (a === 'precedent') choisir(deplacer(visibles, courant, -1));
      else if (a === 'relu') relu();
      else ouvrirNote();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [courant, visibles, choisir, relu, ouvrirNote, noteOuverte, etapeService]);

  if (!actif) return <Navigate to="/" replace />;

  const prog = progression(lignes);
  const parFiltre = Object.fromEntries(
    FILTRES_FILE.map((f) => [f, lignesVisibles(lignes, { etat: f, notion, recherche: '' }).length]),
  ) as Record<FiltreFile, number>;
  const indice = courant === null ? -1 : visibles.findIndex((l) => l.code === courant);
  const ligneCourante = indice < 0 ? null : visibles[indice]!;
  const detail = courant === null ? undefined : details.get(courant);
  const etatCourant = ligneCourante ? etatDansLaFile(ligneCourante.statutRelecture) : null;
  const titreChapitre = reponse.status === 'ready' ? reponse.data.chapitre.titre : '';
  const geste = ligneCourante ? gesteDeService(user?.role, ligneCourante.statut) : null;
  const retrait =
    geste === null || courant === null ? null : (
      <RetraitDuService
        code={courant}
        geste={geste}
        etape={etapeService}
        motif={motifService}
        enCours={enCours}
        onOuvrir={() => setEtapeService(ouvrirGeste(geste))}
        onMotif={setMotifService}
        onContinuer={() => setEtapeService((e) => continuerGeste(e, motifService))}
        onConfirmer={() => void confirmerService()}
        onAnnuler={() => {
          setEtapeService('ferme');
          setMotifService('');
        }}
      />
    );

  return (
    <div className="rx-ecran" data-testid="relecture-exercices">
      <header className="rx-tete">
        <div>
          <div className="rx-fil">
            <Link to="/contenus">Contenus</Link> ›{' '}
            <Link to={`/programme/${encodeURIComponent(code)}`}>{titreChapitre || 'Chapitre'}</Link>
          </div>
          <h1>Relire les exercices{titreChapitre ? ` — ${titreChapitre}` : ''}</h1>
        </div>
        <div className="rx-progression" data-testid="rx-progression">
          <div className="rx-progression__chiffre">
            <b>{direProgression(prog)}</b> lus
          </div>
          <div className="cell-sub">
            {prog.relus} relu{prog.relus > 1 ? 's' : ''} · {prog.aCorriger} à corriger ·{' '}
            {prog.aRelire} à relire
          </div>
          <span className="contenus-barre" aria-hidden="true">
            <span
              className="contenus-barre__relus"
              style={{ width: prog.total ? `${(100 * prog.relus) / prog.total}%` : 0 }}
            />
            <span
              className="contenus-barre__corriger"
              style={{ width: prog.total ? `${(100 * prog.aCorriger) / prog.total}%` : 0 }}
            />
          </span>
        </div>
      </header>

      {reponse.status === 'error' ? <ErreurRessource message={reponse.message} /> : null}

      <div className="rx-volets">
        <aside className="rx-file" aria-label="La file des exercices">
          <div className="rx-file__filtres" role="group" aria-label="État">
            {FILTRES_FILE.map((f) => (
              <button
                key={f}
                type="button"
                className={etat === f ? 'chip-filter is-on' : 'chip-filter'}
                aria-pressed={etat === f}
                onClick={() => poser({ etat: f, ex: null, [PARAM_PAGE]: null })}
              >
                {NOM_FILTRE[f]} <span className="contenus-mono">{parFiltre[f]}</span>
              </button>
            ))}
          </div>
          <div className="rx-file__champs">
            <select
              className="control"
              aria-label="Notion"
              value={notion ?? ''}
              onChange={(e) =>
                poser({ notion: e.target.value || null, ex: null, [PARAM_PAGE]: null })
              }
            >
              <option value="">Toutes les notions</option>
              {notionsDeLaFile(lignes).map((n) => (
                <option key={n.code} value={n.code}>
                  {n.rang === null ? 'Prérequis' : `${n.rang}.`} {n.titre}
                </option>
              ))}
            </select>
            <input
              className="control"
              type="search"
              aria-label="Chercher dans les exercices"
              placeholder="Chercher…"
              value={recherche}
              onChange={(e) => {
                setRecherche(e.target.value);
                poser({ ex: null, [PARAM_PAGE]: null });
              }}
            />
          </div>
          {reponse.status === 'loading' ? (
            <p className="note-line rx-file__vide">Chargement des exercices…</p>
          ) : null}
          {reponse.status === 'ready' && visibles.length === 0 ? (
            <p className="note-line rx-file__vide" data-testid="rx-file-vide">
              Rien dans ce filtre.{etat === 'a_relire' ? ' Tout est lu pour ces notions.' : ''}
            </p>
          ) : null}
          <ul className="rx-file__liste" ref={liste} data-testid="rx-liste">
            {decoupe.elements.map((l) => {
              const e = etatDansLaFile(l.statutRelecture);
              return (
                <li key={l.code} aria-current={l.code === courant ? 'true' : undefined}>
                  <button type="button" className="rx-item" onClick={() => choisir(l.code)}>
                    <span className="rx-item__chapeau">
                      <span>{chapeau(l)}</span>
                      <span>
                        {l.statut === 'retire' ? (
                          <span className="badge badge--a-revoir" data-testid="rx-retire">
                            Retiré
                          </span>
                        ) : null}{' '}
                        <span className={`badge ${TON_ETAT[e]}`}>{NOM_ETAT[e]}</span>
                      </span>
                    </span>
                    <span className="rx-item__enonce">
                      {l.titre ? <b>{l.titre} — </b> : null}
                      <TexteMaths texte={l.enonce.replace(/\n/g, ' ')} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {reponse.status === 'ready' && visibles.length > 0 ? (
            <Pagination
              page={decoupe}
              singulier="exercice"
              onPage={allerALaPage}
              testId="rx-pages"
              classe="pagination--file"
            />
          ) : null}
        </aside>

        <section className="rx-detail" aria-label="L’exercice à relire">
          <div className="rx-detail__corps" ref={volet} data-testid="relecture-volet">
            {ligneCourante?.statut === 'retire' ? (
              <div className="callout callout--rare rx-note-posee" data-testid="rx-retrait-pose">
                <b>Retiré du service</b> — les élèves ne le reçoivent plus.
                {ligneCourante.motifRetrait ? ` Motif : ${ligneCourante.motifRetrait}` : ''}
              </div>
            ) : null}
            {ligneCourante?.noteRelecture && etatCourant === 'a_corriger' ? (
              <div className="callout callout--rare rx-note-posee" data-testid="rx-note-posee">
                <b>Marqué « à corriger » :</b> {ligneCourante.noteRelecture}
              </div>
            ) : null}
            {courant !== null && detail === undefined ? (
              <p className="note-line">Chargement de l’exercice…</p>
            ) : null}
            {detail?.etat === 'erreur' ? <ErreurRessource message={detail.message} /> : null}
            {detail?.etat === 'charge' ? <ExerciceARelire detail={detail.data} /> : null}
          </div>

          <div className="rx-barre" data-testid="rx-barre">
            {noteOuverte ? (
              <div className="rx-barre__note">
                <label htmlFor="relecture-note" className="rx-barre__label">
                  Ce qui est à reprendre — va dans l’historique et au producteur de contenu, jamais
                  au modèle ni à l’élève. La correction se fait dans la source, puis par l’import.
                </label>
                <textarea
                  id="relecture-note"
                  ref={champNote}
                  className="control"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) enregistrerACorriger();
                  }}
                  placeholder="Ex. : l’option c porte un point médian au lieu d’un signe de multiplication."
                />
                <div className="rx-barre__ligne">
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={enCours || note.trim() === ''}
                    onClick={enregistrerACorriger}
                  >
                    Enregistrer « à corriger »
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setNoteOuverte(false)}
                  >
                    Annuler (Échap)
                  </button>
                </div>
              </div>
            ) : null}
            {etapeService !== 'ferme' ? retrait : null}
            <div className="rx-barre__ligne">
              <button
                type="button"
                className="btn btn--sm"
                disabled={courant === null || enCours || etatCourant === 'relu'}
                onClick={relu}
              >
                Relu, rien à changer <kbd>R</kbd>
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={courant === null || enCours}
                onClick={ouvrirNote}
              >
                À corriger… <kbd>C</kbd>
              </button>
              {etatCourant !== null && etatCourant !== 'a_relire' ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={enCours}
                  onClick={remettre}
                >
                  Remettre à relire
                </button>
              ) : null}
              {etapeService === 'ferme' ? retrait : null}
              <span className="rx-barre__nav">
                <span className="cell-sub" data-testid="relecture-position">
                  {indice < 0 ? '—' : `${indice + 1} / ${visibles.length}`}
                </span>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={indice <= 0}
                  onClick={() => choisir(deplacer(visibles, courant, -1))}
                  aria-label="Exercice précédent (K)"
                >
                  ← <kbd>K</kbd>
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={indice < 0 || indice >= visibles.length - 1}
                  onClick={() => choisir(deplacer(visibles, courant, 1))}
                  aria-label="Exercice suivant (J)"
                >
                  <kbd>J</kbd> →
                </button>
              </span>
            </div>
            {erreur ? (
              <div className="callout callout--rare" role="alert">
                {erreur}
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
